import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { DynamicMediaItem } from '@uncinc/react-drupal-core';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import type { VideoJsPlayer } from '@uncinc/uncinc-react-kitchen-sink/dist/components/MediaPlayer/types';

import {
  ActionButton,
  ButtonWrapper,
  ParagraphsRenderer,
  VariableRatiosPicture,
} from '../../../components';
import useLayoutVariables from '../../../hooks/useLayoutVariables';
import type { NodeHomePage as Type } from '../../../types/uncinc_profile/nodes';

import './style.scss';

interface HomePageProps {
  entity: ImmutableEntity<Type>;
  className?: string;
}

const NodeHomePage: React.FC<HomePageProps> = ({ entity, className }) => {
  const { t } = useTranslation();
  const {
    heroImageBreakpointAspectRatios,
    heroTextColClasses,
    heroButtonColClasses,
    heroQuicklinksColClasses,
    quickLinksButtonType,
  } = useLayoutVariables('homePage');

  const videoRef = useRef<VideoJsPlayer | null>(null);
  const [isVideoPaused, setVideoPaused] = useState(false);

  const homePage = entity.toJS();
  const title = homePage.title[0].value;
  const body = homePage.body[0]?.processed;
  const media = homePage.field_media_item[0];

  const link = homePage.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const linkSecondary = homePage.field_link_secondary[0];
  const linkSecondaryTitle = linkSecondary?.title;
  const linkSecondaryUrl = linkSecondary?.alias || linkSecondary?.uri;

  const quickLinks = homePage.field_links;
  const classes = classNames('Node', 'NodeHomePage', className);

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="NodeHomePage__hero">
        <div className="NodeHomePage__hero__media-wrapper">
          {media?.bundle === 'image' && (
            <div className="NodeHomePage__hero__image">
              <VariableRatiosPicture
                image={media}
                aspectRatios={heroImageBreakpointAspectRatios}
              />
            </div>
          )}

          {media?.bundle === 'video' && (
            <div className="NodeHomePage__hero__media">
              <DynamicMediaItem
                media={media}
                cookieWrapper={false}
                mediaProps={{
                  options: {
                    autoplay: true,
                    loop: true,
                    controls: false,
                    // When autoplay is on, both muted and playsinline have to be true in order to play the video
                    // in the background. This will otherwise cause the video to play in fullscreen mode on iOS.
                    muted: true,
                    playsinline: true,
                  },
                  onReady: (player: VideoJsPlayer) => {
                    if (player && videoRef.current !== player) {
                      videoRef.current = player;
                    }
                  },
                }}
              />
            </div>
          )}
        </div>

        <Container>
          <div className="row">
            <div className={heroTextColClasses}>
              <div className="NodeHomePage__hero__text-wrapper">
                <div className="NodeHomePage__hero__text">
                  <h1 itemProp="title" className="NodeHomePage__title">
                    {title}
                  </h1>
                  {body && (
                    <WysiwygField
                      className="NodeHomePage__body body--large"
                      property="schema:text"
                      html={body}
                    />
                  )}
                  {(link || linkSecondary) && (
                    <div className="NodeHomePage__hero__links">
                      {link && (
                        <ButtonWrapper href={linkUrl} inverted>
                          {linkTitle}
                        </ButtonWrapper>
                      )}
                      {linkSecondary && (
                        <ButtonWrapper href={linkSecondaryUrl} type="secondary" inverted>
                          {linkSecondaryTitle}
                        </ButtonWrapper>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={heroButtonColClasses}>
              <div className="NodeHomePage__hero__action-buttons-wrapper">
                <div className="NodeHomePage__hero__action-buttons">
                  {media?.bundle === 'video' && (
                    <ActionButton
                      className="NodeHomePage__hero__play-button"
                      iconName={isVideoPaused ? 'play_arrow' : 'pause'}
                      aria-label={t('components.media_player.pause_video')}
                      onClick={() => {
                        if (!videoRef.current) {
                          return;
                        }

                        if (videoRef.current.paused()) {
                          // eslint-disable-next-line @typescript-eslint/no-floating-promises
                          videoRef.current.play();
                          setVideoPaused(false);
                          return;
                        }

                        videoRef.current.pause();
                        setVideoPaused(true);
                      }}
                    />
                  )}

                  <ActionButton
                    className="NodeHomePage__hero__skip-to-content-button"
                    href="#main"
                    iconName="arrow_downward"
                    aria-label={t('content_types.homepage.skip_to_content')}
                  />
                </div>
              </div>
            </div>

            {quickLinks.length > 0 && (
              <div className={classNames(heroQuicklinksColClasses, 'NodeHomePage__hero__quicklinks-column')}>
                <div className="NodeHomePage__hero__quicklinks-wrapper">
                  <div className="NodeHomePage__hero__quicklinks">
                    <h2>{t('content_types.homepage.quicklinks')}</h2>
                    <div className="NodeHomePage__hero__quicklinks__links">
                      {quickLinks.map((quickLink) => (
                        <ButtonWrapper
                          key={quickLink.title}
                          className="NodeHomePage__hero__quicklinks__link"
                          href={quickLink.alias || quickLink.uri}
                          type={quickLinksButtonType}
                        >
                          {quickLink.title}
                        </ButtonWrapper>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>

      <div id="main">
        <ParagraphsRenderer className="NodeHomePage__paragraphs" paragraphs={entity.get('field_paragraphs')} />
      </div>
    </article>
  );
};

export default NodeHomePage;
